import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, X } from 'lucide-react';
import type { TemplateIndicatorLink } from '../utils/lstIndicator';

export interface ExecucaoIndicatorPayload {
  indicatorRealizedValue: number;
}

interface ExecucaoIndicatorModalProps {
  open: boolean;
  rotinaTitulo: string;
  indicator: TemplateIndicatorLink;
  initialValue?: number | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: ExecucaoIndicatorPayload) => void;
}

/**
 * Modal obrigatório ao concluir rotina com indicador vinculado —
 * captura o valor realizado no gemba para a Nota de Resultado.
 */
export const ExecucaoIndicatorModal: React.FC<ExecucaoIndicatorModalProps> = ({
  open,
  rotinaTitulo,
  indicator,
  initialValue = null,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [valor, setValor] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValor(initialValue != null && Number.isFinite(initialValue) ? String(initialValue) : '');
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const parsed = Number(valor.replace(',', '.'));
  const canSubmit = Number.isFinite(parsed) && !saving;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]"
        onClick={saving ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Informar valor realizado"
        className="relative w-full max-w-[420px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-[var(--bg-surface)]"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
            <BarChart3 size={15} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[13px] font-bold text-slate-800 dark:text-slate-100">Valor Realizado</h2>
            <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{rotinaTitulo}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40 dark:hover:bg-slate-800"
            aria-label="Fechar"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-4 py-3.5">
          <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
            Indicador: <strong className="text-slate-700 dark:text-slate-200">{indicator.name}</strong>
            {indicator.target != null && (
              <span>
                {' '}
                · Meta: {indicator.target}
                {indicator.unit}
              </span>
            )}
          </p>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Informe o valor Realizado <span className="text-red-500">*</span>
          </label>
          <input
            ref={inputRef}
            type="number"
            step="any"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder={`Ex.: ${indicator.target ?? '—'}`}
            className="h-9 w-full rounded-md border border-slate-200 px-3 text-[13px] tabular-nums text-slate-700 outline-none transition-colors focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-700/60">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onSubmit({ indicatorRealizedValue: parsed })}
            disabled={!canSubmit}
            className="rounded-md bg-[#2F6FED] px-3.5 py-1.5 text-[12px] font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? 'Salvando…' : 'Confirmar execução'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExecucaoIndicatorModal;
